/**
 * 🛡️ MODERATION SERVICE
 * Handles vehicle, host, and driver suspension, blacklisting, and reactivation workflows
 */

import { getApiBaseUrl } from '@/config/environment';
import { adminAuthHeaders } from '../utils/adminAuthHeaders';


// ========== TYPES ==========

export type SuspensionStatus = 'active' | 'suspended' | 'blacklisted';
export type EntityType = 'vehicle' | 'host' | 'driver';
export type ActionType = 'suspend' | 'blacklist' | 'reactivate';
export type ApprovalStatus = 'pending' | 'approved' | 'rejected';

export interface ModerationAction {
  id: string;
  entityType: EntityType;
  entityId: string;
  actionType: ActionType;
  reason: string;
  justification?: string;
  performedBy: {
    id: string;
    name: string;
    email: string;
  };
  approvedBy?: {
    id: string;
    name: string;
    email: string;
  };
  approvalStatus: ApprovalStatus;
  performedAt: string;
  approvedAt?: string;
  notes?: string;
}

export interface ReactivationRequest {
  id: string;
  entityType: EntityType;
  entityId: string;
  entityName: string;
  justification: string;
  requestedBy: {
    id: string;
    name: string;
    email: string;
  };
  requestedAt: string;
  notes?: string;
}

export interface SuspendedEntity {
  id: string;
  type: EntityType;
  name: string;
  suspensionStatus: SuspensionStatus;
  suspensionReason: string;
  suspendedAt: string;
  suspendedBy: {
    id: string;
    name: string;
  };
  canReactivate: boolean;
}

// ========== VEHICLE MODERATION ==========

export async function suspendVehicle(
  vehicleId: string,
  reason: string,
  notes?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/admin/vehicles/${vehicleId}/suspend`,
      {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ reason, notes }),
      }
    );

    const result = await response.json();
    if (!response.ok) throw new Error(result.error?.message || 'Failed to suspend vehicle');

    return { success: true };
  } catch (error) {
    console.error('Error suspending vehicle:', error);
    return { success: false, error: (error as Error).message };
  }
}

export async function blacklistVehicle(
  vehicleId: string,
  reason: string,
  adminUserId: string,
  notes?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/admin/vehicles/${vehicleId}/blacklist`,
      {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ adminUserId, reason, notes }),
      }
    );

    const result = await response.json();
    if (!response.ok) throw new Error(result.error?.message || 'Failed to blacklist vehicle');

    return { success: true };
  } catch (error) {
    console.error('Error blacklisting vehicle:', error);
    return { success: false, error: (error as Error).message };
  }
}

export async function requestVehicleReactivation(
  vehicleId: string,
  justification: string,
  notes?: string
): Promise<{ success: boolean; requestId?: string; error?: string }> {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/admin/vehicles/${vehicleId}/request-reactivation`,
      {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ justification, notes }),
      }
    );

    const result = await response.json();
    if (!response.ok) throw new Error(result.error?.message || 'Failed to request reactivation');

    return { success: true, requestId: result.data.reactivationRequestId };
  } catch (error) {
    console.error('Error requesting vehicle reactivation:', error);
    return { success: false, error: (error as Error).message };
  }
}

// ========== HOST MODERATION ==========

export async function suspendHost(
  hostId: string,
  reason: string,
  notes?: string
): Promise<{ success: boolean; vehiclesAffected?: number; error?: string }> {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/admin/hosts/${hostId}/suspend`,
      {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ reason, notes }),
      }
    );

    const result = await response.json();
    if (!response.ok) throw new Error(result.error?.message || 'Failed to suspend host');

    return {
      success: true,
      vehiclesAffected: result.data?.vehicles_affected
    };
  } catch (error) {
    console.error('Error suspending host:', error);
    return { success: false, error: (error as Error).message };
  }
}

// SECURITY UPDATE (Nov 17, 2025): NIN no longer required - backend auto-retrieves it
export async function blacklistHost(
  hostId: string,
  reason: string,
  adminUserId: string,
  notes?: string
): Promise<{ success: boolean; vehiclesAffected?: number; error?: string }> {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/admin/hosts/${hostId}/blacklist`,
      {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ adminUserId, reason, notes }),
      }
    );

    const result = await response.json();
    if (!response.ok) throw new Error(result.error?.message || 'Failed to blacklist host');

    return {
      success: true,
      vehiclesAffected: result.data?.vehicles_affected
    };
  } catch (error) {
    console.error('Error blacklisting host:', error);
    return { success: false, error: (error as Error).message };
  }
}

export async function requestHostReactivation(
  hostId: string,
  justification: string,
  notes?: string
): Promise<{ success: boolean; requestId?: string; error?: string }> {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/admin/hosts/${hostId}/request-reactivation`,
      {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ justification, notes }),
      }
    );

    const result = await response.json();
    if (!response.ok) throw new Error(result.error?.message || 'Failed to request reactivation');

    return { success: true, requestId: result.data.reactivationRequestId };
  } catch (error) {
    console.error('Error requesting host reactivation:', error);
    return { success: false, error: (error as Error).message };
  }
}

// ========== DRIVER MODERATION ==========

export async function suspendDriver(
  driverId: string,
  reason: string,
  notes?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/admin/drivers/${driverId}/suspend`,
      {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ reason, notes }),
      }
    );

    const result = await response.json();
    if (!response.ok) throw new Error(result.error?.message || 'Failed to suspend driver');

    return { success: true };
  } catch (error) {
    console.error('Error suspending driver:', error);
    return { success: false, error: (error as Error).message };
  }
}

export async function blacklistDriver(
  driverId: string,
  reason: string,
  nin: string,
  notes?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/admin/drivers/${driverId}/blacklist`,
      {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ reason, nin, notes }),
      }
    );

    const result = await response.json();
    if (!response.ok) throw new Error(result.error?.message || 'Failed to blacklist driver');

    return { success: true };
  } catch (error) {
    console.error('Error blacklisting driver:', error);
    return { success: false, error: (error as Error).message };
  }
}

export async function requestDriverReactivation(
  driverId: string,
  justification: string,
  notes?: string
): Promise<{ success: boolean; requestId?: string; error?: string }> {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/admin/drivers/${driverId}/request-reactivation`,
      {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ justification, notes }),
      }
    );

    const result = await response.json();
    if (!response.ok) throw new Error(result.error?.message || 'Failed to request reactivation');

    return { success: true, requestId: result.data.reactivationRequestId };
  } catch (error) {
    console.error('Error requesting driver reactivation:', error);
    return { success: false, error: (error as Error).message };
  }
}

// ========== SUPERVISOR ACTIONS ==========

export async function approveReactivation(
  requestId: string,
  notes?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/admin/reactivation-requests/${requestId}/approve`,
      {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ notes }),
      }
    );

    const result = await response.json();
    if (!response.ok) throw new Error(result.error?.message || 'Failed to approve reactivation');

    return { success: true };
  } catch (error) {
    console.error('Error approving reactivation:', error);
    return { success: false, error: (error as Error).message };
  }
}

export async function rejectReactivation(
  requestId: string,
  reason: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/admin/reactivation-requests/${requestId}/reject`,
      {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ reason }),
      }
    );

    const result = await response.json();
    if (!response.ok) throw new Error(result.error?.message || 'Failed to reject reactivation');

    return { success: true };
  } catch (error) {
    console.error('Error rejecting reactivation:', error);
    return { success: false, error: (error as Error).message };
  }
}

// ========== DASHBOARD QUERIES ==========

export async function getSuspendedEntities(
  entityType: EntityType | 'all' = 'all',
  status: 'suspended' | 'blacklisted' = 'suspended',
  page: number = 1,
  limit: number = 50
): Promise<{ success: boolean; data?: any; error?: string }> {
  try {
    const params = new URLSearchParams({
      entity_type: entityType,
      status,
      page: page.toString(),
      limit: limit.toString(),
    });

    const response = await fetch(
      `${getApiBaseUrl()}/api/admin/moderation/suspended?${params}`,
      {
        method: 'GET',
        headers: adminAuthHeaders(),
      }
    );

    const result = await response.json();
    if (!response.ok) throw new Error(result.error?.message || 'Failed to fetch suspended entities');

    return { success: true, data: result.data };
  } catch (error) {
    console.error('Error fetching suspended entities:', error);
    return { success: false, error: (error as Error).message };
  }
}

export async function getPendingReactivationRequests(): Promise<{
  success: boolean;
  data?: ReactivationRequest[];
  error?: string;
}> {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/admin/reactivation-requests/pending`,
      {
        method: 'GET',
        headers: adminAuthHeaders(),
      }
    );

    const result = await response.json();
    if (!response.ok) throw new Error(result.error?.message || 'Failed to fetch reactivation requests');

    return { success: true, data: result.data.requests };
  } catch (error) {
    console.error('Error fetching reactivation requests:', error);
    return { success: false, error: (error as Error).message };
  }
}

export async function getModerationHistory(
  entityType?: EntityType,
  entityId?: string,
  actionType?: ActionType,
  fromDate?: string,
  toDate?: string
): Promise<{ success: boolean; data?: ModerationAction[]; error?: string }> {
  try {
    const params = new URLSearchParams();
    if (entityType) params.append('entity_type', entityType);
    if (entityId) params.append('entity_id', entityId);
    if (actionType) params.append('action_type', actionType);
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);

    const response = await fetch(
      `${getApiBaseUrl()}/api/admin/moderation/history?${params}`,
      {
        method: 'GET',
        headers: adminAuthHeaders(),
      }
    );

    const result = await response.json();
    if (!response.ok) throw new Error(result.error?.message || 'Failed to fetch moderation history');

    return { success: true, data: result.data.actions };
  } catch (error) {
    console.error('Error fetching moderation history:', error);
    return { success: false, error: (error as Error).message };
  }
}
